import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PageableUtilService } from '@services/util';
import {
  IssueComment,
  IssueCommentFilterParams,
  IssueCommentPayload,
  PageableData,
  TableParams,
} from '@shared/models';
import { Observable } from 'rxjs';

/**
 * The issue thread, backed entirely by issue-comment-resource in the assets
 * API. One method per operation the resource exposes; nothing else belongs here.
 *
 * Note the API models a flat comment list — there is no parent or reply field on
 * IssueCommentDto, so replies cannot be represented server-side.
 */
@Injectable({
  providedIn: 'root',
})
export class ThreadService {
  private API_URL = `/api/assets/issue`;

  constructor(
    private httpClient: HttpClient,
    private util: PageableUtilService,
  ) {}

  /** GET /issue/{issueCode}/comment */
  public getComments(
    issueCode: string,
    tableParams: TableParams,
    filters?: IssueCommentFilterParams,
  ): Observable<PageableData<IssueComment>> {
    const params = this.util.buildParams(tableParams, filters);

    return this.httpClient.get<PageableData<IssueComment>>(
      `${this.API_URL}/${issueCode}/comment`,
      { params },
    );
  }

  /** POST /issue/{issueCode}/comment */
  public createComment(
    issueCode: string,
    payload: IssueCommentPayload,
  ): Observable<IssueComment> {
    return this.httpClient.post<IssueComment>(
      `${this.API_URL}/${issueCode}/comment`,
      payload,
    );
  }

  /** PUT /issue/{issueCode}/comment/{code} */
  public updateComment(
    issueCode: string,
    code: string,
    payload: IssueCommentPayload,
  ): Observable<IssueComment> {
    return this.httpClient.put<IssueComment>(
      `${this.API_URL}/${issueCode}/comment/${code}`,
      payload,
    );
  }

  /** DELETE /issue/{issueCode}/comment/{code} */
  public deleteComment(
    issueCode: string,
    code: string,
  ): Observable<IssueComment> {
    return this.httpClient.delete<IssueComment>(
      `${this.API_URL}/${issueCode}/comment/${code}`,
    );
  }

  /**
   * POST /issue/{issueCode}/comment/attachment
   *
   * Uploads the file on its own and returns the server's key/value response.
   * The resulting reference still has to be posted as a comment via
   * createComment, so the upload and the comment are two separate calls.
   */
  public uploadAttachment(
    issueCode: string,
    file: File,
  ): Observable<Record<string, string>> {
    const body = new FormData();
    body.append('file', file);

    return this.httpClient.post<Record<string, string>>(
      `${this.API_URL}/${issueCode}/comment/attachment`,
      body,
    );
  }
}
